import * as React from "react";
import Box from "@mui/material/Box";
import {useContext, useEffect, useState} from "react";
import {TokenContext} from "../../Context/TokenContext";
import {
    Card,
    CardContent,
    Dialog,
    Alert,
    AlertTitle,
    CircularProgress
} from "@mui/material";
import Typography from "@mui/material/Typography";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import {useNavigate} from "react-router";
import {useTranslation} from "../../hooks/useTranslation";

export function Authentication() {
    const {
        isAuthenticated,
        isLoading,
        authError,
        finishProcess,
        startOAuthFlow,
        setCode,
        clearAuth
    } = useContext(TokenContext);

    const navigate = useNavigate();
    const t = useTranslation('Auth.json');
    const tCommon = useTranslation('Common.json');
    const [hasProcessed, setHasProcessed] = useState(false);
    const [trying, setTrying] = useState(0);

    // Traiter les paramètres de callback OAuth
    useEffect(() => {
        console.log('🔍 [Authentication] État actuel:', {
            hasProcessed,
            isAuthenticated,
            finishProcess,
            isLoading
        });

        // Si déjà authentifié, ne rien faire
        if (isAuthenticated && finishProcess) {
            console.log('✅ [Authentication] Déjà authentifié, attente redirection');
            return;
        }

        console.log(
            isAuthenticated,
            finishProcess,
            isLoading)
        if (hasProcessed) {
            console.log('⏭️ [Authentication] Déjà traité, en attente');
            if(trying <0){
                console.log('⏭️ [Authentication] Nouvelle tentative');
                handleRetry()
            }
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        console.log('📋 [Authentication] Paramètres URL:', {
            code: code ? code.substring(0, 10) + '...' : 'null',
            state: state ? state.substring(0, 10) + '...' : 'null',
            error
        });

        // Cas 1: Erreur OAuth
        if (error) {
            console.error('❌ [Authentication] Erreur OAuth:', errorDescription || error);
            setHasProcessed(true);
            return;
        }
        // Cas 2: Code d'autorisation reçu
        if (code) {
            console.log('🔐 [Authentication] Code d\'autorisation reçu, traitement...');
            setCode(code, state);
            setHasProcessed(true);

            // Nettoyer l'URL
            console.log('🧹 [Authentication] Nettoyage de l\'URL');
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        // Cas 3: Pas de paramètres, démarrer le flux OAuth
        if (!code && !error && !isAuthenticated) {
            console.log('🚀 [Authentication] Pas de code et pas authentifié, démarrage du flux OAuth dans 1s');
            const timer = setTimeout(() => {
                startOAuthFlow();
            }, 1000);
            return () => clearTimeout(timer);
        }

        console.log('⚠️ [Authentication] Aucune action nécessaire');
    }, [hasProcessed, setCode, startOAuthFlow, isAuthenticated, finishProcess, isLoading]);

    // Redirection après succès
    useEffect(() => {
        if (isAuthenticated && finishProcess) {
            console.log('✅ Authentifié, redirection vers l\'accueil');
            setTimeout(() => {
                navigate('/', { replace: true });
            }, 1500);
        }
    }, [isAuthenticated, finishProcess, navigate]);

    const handleRetry = () => {
        console.log('🔄 Nouvelle tentative d\'authentification');
        clearAuth();
        setHasProcessed(false);
        startOAuthFlow();
    };

    const handleClose = () => {
        console.log('❌ Fermeture');
        navigate('/', { replace: true });
    };

    return (
        <Box width={'100vw'} height={'100vh'} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #FF7233 0%, #B23A0F 100%)'
        }}>
            <Dialog
                open={true}
                fullWidth={true}
                maxWidth={'sm'}
                disableEscapeKeyDown
            >
                <Card>
                    <CardContent sx={{ p: 4 }}>
                        {authError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <AlertTitle>{t('auth.error.title')}</AlertTitle>
                                {authError}
                            </Alert>
                        )}

                        {isAuthenticated && finishProcess ? (
                            <Box textAlign="center">
                                <Typography variant="h5" gutterBottom color="success.main">
                                    {t('auth.success.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('auth.success.redirecting')}
                                </Typography>
                                <CircularProgress size={24} sx={{ mt: 2 }} />
                            </Box>
                        ) : isLoading ? (
                            <Box textAlign="center">
                                <Typography variant="h5" gutterBottom>
                                    {t('auth.checking.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('auth.checking.subtitle')}
                                </Typography>
                                <CircularProgress />
                            </Box>
                        ) : !hasProcessed && !authError ? (
                            <Box textAlign="center">
                                <Typography variant="h5" gutterBottom>
                                    {t('auth.redirecting.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('auth.redirecting.subtitle')}
                                </Typography>
                                <CircularProgress />
                            </Box>
                        ) : null}
                    </CardContent>

                    {authError && (
                        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                            <Button onClick={handleClose} color="inherit">
                                {tCommon('common.close')}
                            </Button>
                            <Button
                                onClick={handleRetry}
                                variant="contained"
                                color="primary"
                            >
                                {t('auth.retry')}
                            </Button>
                        </CardActions>
                    )}
                </Card>
            </Dialog>

            <svg
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '15vh',
                    zIndex: 0
                }}
                className="waves"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="0 24 150 28"
                preserveAspectRatio="none"
                shapeRendering="auto"
            >
                <defs>
                    <path id="gentle-wave"
                          d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"/>
                </defs>
                <g className="parallax">
                    <use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7)"/>
                    <use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.5)"/>
                    <use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.3)"/>
                    <use xlinkHref="#gentle-wave" x="48" y="7" fill="#fff"/>
                </g>
            </svg>
        </Box>
    );
}
